import React from 'react';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold text-black mb-3">{title}</h2>
    <div className="space-y-3 text-gray-700 leading-relaxed text-[15px]">{children}</div>
  </section>
);

const Terms = () => (
  <div className="min-h-screen pt-32 pb-20 px-6 bg-white">
    <div className="max-w-3xl mx-auto">
      <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Legal</p>
      <h1 className="text-4xl md:text-5xl font-bold text-black mb-2">Terms &amp; Conditions</h1>
      <p className="text-sm text-gray-500 mb-10">Last updated: May 2026</p>

      <Section title="1. About these terms">
        <p>
          These terms govern your use of the Obsidian Chauffeurs website and the chauffeur services we
          provide. By requesting a quote, making a booking or creating an account, you agree to them.
          If you book on behalf of someone else, you confirm you have their authority to do so.
        </p>
      </Section>

      <Section title="2. Quotes &amp; pricing">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>All prices include tolls and parking.</li>
          <li>Quotes are based on the journey details you give us (date, time, pickup, drop-off, number of passengers).</li>
          <li>Any changes to the route or extra stops may affect the price.</li>
          <li>Longer waiting time or extra mileage on the day may be charged in addition to the original quote.</li>
          <li>Quotes are valid for 7 days from the date of issue.</li>
          <li>Prices are quoted in GBP (£) and include VAT where applicable.</li>
        </ul>
      </Section>

      <Section title="3. Bookings &amp; confirmation">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Accepted quotes must be confirmed by you; journeys are not guaranteed without confirmation.</li>
          <li>A confirmation email containing your booking reference will be sent to the email address you provided.</li>
          <li>We reserve the right to decline any booking that we cannot reasonably fulfil.</li>
        </ul>
      </Section>

      <Section title="4. Cancellations &amp; amendments">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Free cancellation up to 24 hours before the scheduled pickup time.</li>
          <li>Cancellations made less than 24 hours before pickup may be charged at up to 100% of the quoted fare to cover the chauffeur's reserved time.</li>
          <li>Amendments to date, time or route can be made up to 24 hours before pickup, subject to availability; a re-quote may apply.</li>
        </ul>
      </Section>

      <Section title="5. Waiting time, delays &amp; no-shows">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>For airport pickups, we monitor your flight and provide up to 60 minutes' free waiting time from the actual landing time.</li>
          <li>For all other pickups, 15 minutes' free waiting time is included.</li>
          <li>Additional waiting time is charged at our standard hourly rate, pro-rated by the minute.</li>
          <li>If the passenger cannot be located or contacted within 30 minutes of the scheduled pickup, the booking will be treated as a no-show and the full fare will be charged.</li>
        </ul>
      </Section>

      <Section title="6. Passenger conduct &amp; vehicles">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Smoking, vaping and consumption of food are not permitted in our vehicles.</li>
          <li>Alcohol may only be consumed with the chauffeur's express permission and where lawful.</li>
          <li>You are responsible for any damage caused to the vehicle by you, your passengers or your luggage. Valet charges of up to £150 may apply for soiling that prevents the next booking.</li>
          <li>Our chauffeurs reserve the right to refuse or terminate a journey if a passenger's behaviour is unsafe, threatening or abusive.</li>
          <li>All passengers must wear seatbelts at all times, as required by UK law.</li>
        </ul>
      </Section>

      <Section title="7. Luggage &amp; child seats">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Please tell us the number of bags at the time of booking so we can allocate the right vehicle.</li>
          <li>We carry suitcases and standard luggage; oversized or unusual items must be agreed in advance.</li>
          <li>Child seats can be provided on request — please specify the child's age when booking.</li>
        </ul>
      </Section>

      <Section title="8. Liability">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>We will use reasonable endeavours to deliver you on time, but we cannot be held responsible for delays caused by traffic, weather, road closures or other events outside our reasonable control.</li>
          <li>We are not liable for any indirect or consequential loss arising from a missed connection, flight or appointment.</li>
          <li>Items left in our vehicles are held for 30 days; we will make reasonable efforts to return them but accept no liability for loss.</li>
          <li>Nothing in these terms limits our liability for death or personal injury caused by our negligence, or for fraud.</li>
        </ul>
      </Section>

      <Section title="9. Payment">
        <p>
          Unless otherwise agreed in writing, payment is due at the end of the journey by card, bank
          transfer or any other method we accept at the time. Corporate accounts may be invoiced subject
          to credit approval.
        </p>
      </Section>

      <Section title="10. Complaints">
        <p>
          We aim to resolve any concerns quickly and fairly. Please email{' '}
          <a href="mailto:info@obsidianchauffeurs.co.uk" className="underline">info@obsidianchauffeurs.co.uk</a>{' '}
          within 14 days of the journey and we will respond within 5 working days.
        </p>
      </Section>

      <Section title="11. Governing law">
        <p>
          These terms are governed by the laws of England &amp; Wales. Any disputes will be subject to
          the non-exclusive jurisdiction of the English courts.
        </p>
      </Section>
    </div>
  </div>
);

export default Terms;
